import { isAuthRoute } from "@/lib/auth/routes";
import { resolveBaseUrl } from "./config";
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from "./tokens";

/**
 * Typed fetch wrapper for the License Guard admin API.
 *
 *  - Prefixes the resolved base URL.
 *  - Injects `Authorization: Bearer <access>`.
 *  - On a 401, performs a single de-duplicated refresh and retries once. If the
 *    refresh fails, clears tokens and returns to /login.
 *  - Passes FormData through untouched, so multipart uploads need no special
 *    case at the call site.
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Return the raw Response (for blob downloads) instead of parsed JSON. */
  raw?: boolean;
  /** Skip the Authorization header (login, refresh). */
  skipAuth?: boolean;
  /** Internal: prevents infinite refresh recursion. */
  _isRetry?: boolean;
}

// Single-flight refresh: concurrent 401s share one refresh request.
let refreshPromise: Promise<boolean> | null = null;

function buildQuery(
  params?: Record<string, string | number | boolean | undefined | null>
): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      usp.append(key, String(value));
    }
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Every admin path must end in `/`.
 *
 * DRF's routers emit trailing slashes and Django's APPEND_SLASH is on, so a
 * slashless POST answers 301 — which the browser replays as a GET, dropping the
 * body without raising anything. A silently discarded write is worse than a
 * loud failure, so this asserts rather than repairs.
 */
function assertTrailingSlash(path: string): void {
  const [bare] = path.split("?");
  if (!bare.endsWith("/")) {
    throw new Error(
      `API path "${path}" must end with "/" — without it Django redirects the ` +
        `request and the browser retries it as a GET, losing the body.`
    );
  }
}

function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  // Never navigate when already on an auth page: that assignment is a full page
  // load, so on /login it is a reload, and any unauthenticated request made
  // while that page renders would 401 and reload again, about once a second.
  if (isAuthRoute(window.location.pathname)) return;
  window.location.href = "/login";
}

async function refreshTokens(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;

  const baseUrl = await resolveBaseUrl();
  try {
    // SimpleJWT's contract: `refresh` in, `{access, refresh}` out. The new
    // refresh token must be stored — ROTATE_REFRESH_TOKENS is on and the old
    // one is blacklisted the moment this succeeds.
    const res = await fetch(`${baseUrl}/auth/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.access) return false;
    setTokens(data.access, data.refresh);
    return true;
  } catch {
    return false;
  }
}

function extractMessage(status: number, payload: unknown): string {
  if (payload && typeof payload === "object") {
    const body = payload as Record<string, unknown>;
    // DRF's own shape first.
    if (typeof body.detail === "string") return body.detail;
    // Then field errors: {"email": ["Enter a valid email address."]}.
    for (const value of Object.values(body)) {
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
      if (typeof value === "string") return value;
    }
  }
  if (typeof payload === "string" && payload) return payload;
  return `Request failed with status ${status}`;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiOptions = {}
): Promise<T> {
  const { body, params, raw, skipAuth, _isRetry, headers, ...rest } = options;
  assertTrailingSlash(path);

  const baseUrl = await resolveBaseUrl();
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  const finalHeaders: Record<string, string> = {
    ...(headers as Record<string, string>),
  };
  // Never set Content-Type for FormData — the browser has to write the
  // multipart boundary itself.
  if (body !== undefined && !isFormData) {
    finalHeaders["Content-Type"] = "application/json";
  }
  if (!skipAuth) {
    const token = getAccessToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${baseUrl}${path}${buildQuery(params)}`, {
    ...rest,
    headers: finalHeaders,
    body: isFormData ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !skipAuth && !_isRetry) {
    if (!refreshPromise) {
      refreshPromise = refreshTokens().finally(() => {
        refreshPromise = null;
      });
    }
    const ok = await refreshPromise;
    if (ok) {
      return apiFetch<T>(path, { ...options, _isRetry: true });
    }
    clearTokens();
    redirectToLogin();
    throw new ApiError(401, "Your session has expired. Please sign in again.");
  }

  if (raw) {
    if (!res.ok) throw new ApiError(res.status, `Request failed with status ${res.status}`);
    return res as unknown as T;
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let payload: unknown = text;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      /* keep the raw text */
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, extractMessage(res.status, payload), payload);
  }
  return payload as T;
}

export const api = {
  get: <T = unknown>(path: string, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "POST", body }),
  put: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "PUT", body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "PATCH", body }),
  delete: <T = unknown>(path: string, options?: ApiOptions) =>
    apiFetch<T>(path, { ...options, method: "DELETE" }),
};
